import { useState } from 'react'
import toast from 'react-hot-toast'
import {Copy, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { PROTOCOLOS_OLEOS, instrucaoInternaAgua } from '@/utils/protocoloUso'
import { copiarTexto } from '@/utils/clipboard'

interface ProtocoloOleosQueTemhoProps {
  lead: any
}

// Lista de óleos disponíveis com mapeamento para chaves do protocolo
const OLEOS_DISPONIVEIS = [
  { nome: 'Lavanda', chave: 'Lavanda' },
  { nome: 'Hortelã-Pimenta', chave: 'Hortelã-Pimenta' },
  { nome: 'Limão Siciliano', chave: 'Limão Siciliano' },
  { nome: 'Tangerina', chave: 'Tangerina' },
  { nome: 'Copaíba', chave: 'Copaíba' },
  { nome: 'Olíbano', chave: 'Olíbano' },
  { nome: 'Melaleuca', chave: 'Melaleuca' },
  { nome: 'Mix de Proteção (On Guard)', chave: 'Mix Imunidade' },
  { nome: 'Mix Calmante (Deep Blue)', chave: 'Mix para Tensão e Dores' },
  { nome: 'Mix para Aterramento (Balance)', chave: 'Mix para Aterramento' },
  { nome: 'Mix Respiratório (Breathe)', chave: 'Mix Respiratório' },
  { nome: 'Mix Digestivo (Zengest)', chave: 'Mix Digestivo' },
  { nome: 'Mix Metabólico (Meta PWR)', chave: 'Mix Metabólico' },
  { nome: 'Mix Mensal Feminino (ClaryCalm)', chave: 'Mix Mensal Feminino' },
]

// Classificação dos óleos por período (usando as chaves do protocolo)
// Ordem fixa da manhã: Hortelã → Meta PWR → Limão → Olíbano → Copaíba → On Guard
const OLEOS_MANHA = [
  'Hortelã-Pimenta',
  'Mix Metabólico',
  'Limão Siciliano',
  'Olíbano',
  'Copaíba',
  'Mix Imunidade',
]

const OLEOS_DIA = [
  'Mix para Aterramento',
  'Tangerina',
  'Mix Respiratório',
  'Mix Metabólico', // segunda dose
  'Copaíba', // segunda dose
  'Olíbano', // segunda dose
  'Limão Siciliano', // quando houver conflito com Meta PWR, uso aromático no dia
]

const OLEOS_NOITE = [
  'Lavanda',
  'Tangerina',
  'Mix para Aterramento',
  'Copaíba',
  'Mix Mensal Feminino',
  'Mix Digestivo',
]

// Instruções por óleo e período
const INSTRUCOES_ROTINA: Record<string, { manha?: string; dia?: string; noite?: string }> = {
  'Hortelã-Pimenta': {
    manha: 'inalar 1 gota para energia e foco ao acordar.',
  },
  'Limão Siciliano': {
    manha: `${instrucaoInternaAgua('após o café da manhã')} Usar garrafa de vidro, nunca plástico.`,
    dia: 'inalar 1 gota para clareza mental e leveza.',
  },
  'Mix Metabólico': {
    manha: `${instrucaoInternaAgua('30 minutos antes do café da manhã')} Usar garrafa de vidro — nunca plástico.`,
    dia: `${instrucaoInternaAgua('30 minutos antes do almoço e no final da tarde')} Usar garrafa de vidro — nunca plástico.`,
  },
  'Mix Imunidade': {
    manha: instrucaoInternaAgua('após o café da manhã'),
  },
  'Olíbano': {
    manha: '1 gota sublingual após o café da manhã.',
    dia: '1 gota sublingual após o almoço.',
  },
  'Copaíba': {
    manha: '1 gota sublingual após o café da manhã.',
    dia: '1 gota sublingual após o almoço.',
    noite: '1 gota sublingual após o jantar.',
  },
  'Mix para Aterramento': {
    dia: 'aplicar na nuca ou pulsos sempre que sentir ansiedade ou estresse.',
    noite: 'aplicar na nuca e sola dos pés antes de dormir.',
  },
  'Tangerina': {
    dia: 'inalar 1 gota para leveza e alegria.',
    noite: `${instrucaoInternaAgua('após o jantar para uma noite repousante')} ${instrucaoInternaAgua('2 a 3 vezes ao dia para imunidade')}`,
  },
  'Mix Respiratório': {
    dia: 'inalar ou aplicar no peito em momentos de tensão.',
  },
  'Lavanda': {
    noite: 'inalar 1 gota ou aplicar no peito, nuca e pulsos antes de dormir.',
  },
  'Mix Mensal Feminino': {
    noite: 'aplicar no abdômen ou pulsos à noite.',
  },
  'Mix Digestivo': {
    noite: instrucaoInternaAgua('após o jantar, se necessário'),
  },
}

export default function ProtocoloOleosQueTemho({ lead }: ProtocoloOleosQueTemhoProps) {
  const [oleosSelecionados, setOleosSelecionados] = useState<string[]>([])
  const [protocoloGerado, setProtocoloGerado] = useState(false)

  // Toggle seleção de óleo
  const toggleOleo = (chave: string) => {
    setOleosSelecionados(prev =>
      prev.includes(chave) ? prev.filter(o => o !== chave) : [...prev, chave]
    )
  }

  // Gerar protocolo de uso (Bloco 1)
  const gerarProtocoloUso = (): string => {
    let texto = `*${lead.nome?.trim() || ''}*\n`
    texto += '*PROTOCOLO DE USO — ÓLEOS QUE VOCÊ JÁ TEM*\n\n'

    oleosSelecionados.forEach(chave => {
      const protocolo = PROTOCOLOS_OLEOS[chave]
      if (!protocolo) return

      texto += `*${protocolo.nome}*\n`
      texto += `*Indicado:* ${protocolo.indicado}\n`

      if (protocolo.aromatico) {
        texto += `*Aromático:* ${protocolo.aromatico}\n`
      }

      if (protocolo.topico) {
        texto += `*Tópico:* ${protocolo.topico}\n`
      }

      if (protocolo.topicoGordura) {
        texto += `*Tópico para gordura localizada:* ${protocolo.topicoGordura}\n`
      }

      if (protocolo.topicoHormonal) {
        texto += `*Tópico para equilíbrio hormonal:* ${protocolo.topicoHormonal}\n`
      }

      if (protocolo.topicoOndas) {
        texto += `*Tópico para ondas de calor:* ${protocolo.topicoOndas}\n`
      }

      if (protocolo.interno) {
        texto += `*Interno:* ${protocolo.interno}\n`
      }

      if (protocolo.usoDireto) {
        texto += `*Uso direto:* ${protocolo.usoDireto}\n`
      }

      if (protocolo.atencao) {
        texto += `*Atenção:* ${protocolo.atencao}\n`
      }

      texto += '\n'
    })

    return texto
  }

  // Gerar rotina diária (Bloco 2)
  const gerarRotinaDiaria = (): string => {
    let texto = '*ROTINA DIÁRIA COM OS ÓLEOS QUE VOCÊ TEM*\n\n'

    // Detectar conflito Limão Siciliano x Mix Metabólico (Meta PWR já contém limão)
    const temMetaPWR = oleosSelecionados.includes('Mix Metabólico')
    const temLimao = oleosSelecionados.includes('Limão Siciliano')
    const conflitoLimaoMetaPWR = temMetaPWR && temLimao

    // MANHÃ — respeita ordem fixa; remove Limão se houver conflito
    const oleosManha = OLEOS_MANHA.filter(oleo => {
      if (!oleosSelecionados.includes(oleo)) return false
      if (conflitoLimaoMetaPWR && oleo === 'Limão Siciliano') return false
      return true
    })
    if (oleosManha.length > 0) {
      texto += '*MANHÃ — Energia, foco e proteção*\n'
      oleosManha.forEach(oleo => {
        const instrucao = INSTRUCOES_ROTINA[oleo]?.manha
        if (instrucao) {
          const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
          texto += `- ${nomeExibicao} → ${instrucao}\n`
        }
      })
      texto += '\n'
    }

    // DIA — inclui Limão Siciliano aromático apenas se houver conflito
    const oleosDia = OLEOS_DIA.filter(oleo => {
      if (!oleosSelecionados.includes(oleo)) return false
      if (oleo === 'Limão Siciliano' && !conflitoLimaoMetaPWR) return false
      return true
    })
    if (oleosDia.length > 0) {
      texto += '*DIA — Equilíbrio emocional e suporte contínuo*\n'
      oleosDia.forEach(oleo => {
        const instrucao = INSTRUCOES_ROTINA[oleo]?.dia
        if (instrucao) {
          const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
          texto += `- ${nomeExibicao} → ${instrucao}\n`
        }
      })
      texto += '\n'
    }

    // NOITE
    const oleosNoite = OLEOS_NOITE.filter(oleo => oleosSelecionados.includes(oleo))
    if (oleosNoite.length > 0) {
      texto += '*NOITE — Descanso, relaxamento e regeneração*\n'
      oleosNoite.forEach(oleo => {
        const instrucao = INSTRUCOES_ROTINA[oleo]?.noite
        if (instrucao) {
          const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
          texto += `- ${nomeExibicao} → ${instrucao}\n`
        }
      })
      texto += '\n'
    }

    texto += '*Observação:* cada pessoa é única. Respeite como seu corpo reage a cada óleo e ajuste conforme sua necessidade. Na dúvida, entre em contato para orientação personalizada.'

    return texto
  }

  // Gerar protocolo completo
  const handleGerarProtocolo = () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um óleo')
      return
    }
    setProtocoloGerado(true)
    toast.success('Protocolo gerado!')
  }

  // Copiar protocolo de uso
  const copiarProtocoloUso = async () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione óleos primeiro')
      return
    }
    const texto = gerarProtocoloUso()
    try {
      await copiarTexto(texto)
      toast.success('Protocolo de uso copiado!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  // Copiar rotina diária
  const copiarRotinaDiaria = async () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione óleos primeiro')
      return
    }
    const texto = gerarRotinaDiaria()
    try {
      await copiarTexto(texto)
      toast.success('Rotina diária copiada!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  // Copiar tudo
  const copiarTudo = async () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione óleos primeiro')
      return
    }
    const protocolo = gerarProtocoloUso()
    const rotina = gerarRotinaDiaria()
    const texto = `${protocolo}\n${rotina}`
    try {
      await copiarTexto(texto)
      toast.success('Protocolo completo copiado!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  // Formatar número para WhatsApp
  const formatarNumeroWhatsApp = (whatsapp: string): string => {
    const apenasDigitos = (whatsapp || '').replace(/\D/g, '')
    if (!apenasDigitos) return ''
    if (apenasDigitos.startsWith('55') && apenasDigitos.length >= 12) return apenasDigitos
    if (apenasDigitos.length >= 10 && apenasDigitos.length <= 11) return `55${apenasDigitos}`
    return apenasDigitos
  }

  // Abrir no WhatsApp
  const abrirWhatsApp = () => {
    if (oleosSelecionados.length === 0) {
      toast.error('Selecione óleos primeiro')
      return
    }
    const protocolo = gerarProtocoloUso()
    const rotina = gerarRotinaDiaria()
    const mensagemFormatada = `${protocolo}\n${rotina}`
    const numero = formatarNumeroWhatsApp(lead.whatsapp)
    if (!numero) {
      toast.error('WhatsApp da cliente não disponível')
      return
    }
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(mensagemFormatada)}`
    window.open(url, '_blank')
  }

  return (
    <div className="border-t border-bege-medio pt-6">
      <h3 className="text-lg font-semibold text-texto-escuro mb-4">
        Protocolo de Óleo Personalizado (Óleos comprados ou em estoque)
      </h3>

      <div className="bg-bege-fundo p-6 rounded-xl mb-6">
        <p className="text-sm text-texto-suave mb-4">
          Selecione os óleos que a cliente já possui para gerar um protocolo personalizado:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {OLEOS_DISPONIVEIS.map(({ nome, chave }) => (
            <label
              key={chave}
              className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-verde-muito-claro transition-colors"
            >
              <input
                type="checkbox"
                checked={oleosSelecionados.includes(chave)}
                onChange={() => toggleOleo(chave)}
                className="w-5 h-5 text-verde-escuro rounded focus:ring-verde-claro"
              />
              <span className="text-sm text-texto-escuro">{nome}</span>
            </label>
          ))}
        </div>

        <button
          onClick={handleGerarProtocolo}
          disabled={oleosSelecionados.length === 0}
          className="w-full bg-verde-escuro text-white py-3 rounded-xl font-medium hover:bg-opacity-90 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Gerar protocolo com esses óleos ({oleosSelecionados.length} selecionados)
        </button>
      </div>

      {protocoloGerado && oleosSelecionados.length > 0 && (
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-xl border-2 border-verde-claro space-y-5">
            <div>
              <h4 className="font-semibold text-verde-escuro mb-1">
                Protocolo gerado com {oleosSelecionados.length} óleo(s)
              </h4>
              <p className="text-sm text-texto-suave">
                Confira abaixo o texto completo de cada óleo, o modo de uso e quando usar. Depois use os botões para copiar ou enviar pelo WhatsApp.
              </p>
            </div>

            <div className="grid gap-4">
              {oleosSelecionados.map((chave) => {
                const protocolo = PROTOCOLOS_OLEOS[chave]
                if (!protocolo) return null

                return (
                  <div
                    key={chave}
                    className="rounded-xl border p-5 space-y-3"
                    style={{ borderColor: '#52B788', backgroundColor: '#FAFFFE' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 rounded-full" style={{ backgroundColor: '#52B788' }} />
                      <h5
                        className="font-semibold text-base"
                        style={{ color: '#2D6A4F', fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        {protocolo.nome}
                      </h5>
                    </div>

                    <div className="space-y-2 text-sm" style={{ color: '#2C2C2A' }}>
                      <p>
                        <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                          Indicado:
                        </strong>{' '}
                        {protocolo.indicado}
                      </p>

                      {protocolo.aromatico ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Aromático:
                          </strong>{' '}
                          {protocolo.aromatico}
                        </p>
                      ) : null}

                      {protocolo.topico ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Tópico:
                          </strong>{' '}
                          {protocolo.topico}
                        </p>
                      ) : null}

                      {protocolo.topicoGordura ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Tópico para gordura localizada:
                          </strong>{' '}
                          {protocolo.topicoGordura}
                        </p>
                      ) : null}

                      {protocolo.topicoHormonal ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Tópico para equilíbrio hormonal:
                          </strong>{' '}
                          {protocolo.topicoHormonal}
                        </p>
                      ) : null}

                      {protocolo.topicoOndas ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Tópico para ondas de calor:
                          </strong>{' '}
                          {protocolo.topicoOndas}
                        </p>
                      ) : null}

                      {protocolo.interno ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Interno:
                          </strong>{' '}
                          {protocolo.interno}
                        </p>
                      ) : null}

                      {protocolo.usoDireto ? (
                        <p>
                          <strong className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B08968' }}>
                            Uso direto:
                          </strong>{' '}
                          {protocolo.usoDireto}
                        </p>
                      ) : null}

                      {protocolo.atencao ? (
                        <div
                          className="rounded-lg p-3"
                          style={{ backgroundColor: '#FFFBEB', borderLeft: '3px solid #FDE68A' }}
                        >
                          <p className="text-xs" style={{ color: '#92400E' }}>
                            <strong>Atenção:</strong> {protocolo.atencao}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>

            {(() => {
              const temMetaPWR = oleosSelecionados.includes('Mix Metabólico')
              const temLimao = oleosSelecionados.includes('Limão Siciliano')
              const conflitoLimaoMetaPWR = temMetaPWR && temLimao
              const oleosManha = OLEOS_MANHA.filter((oleo) => {
                if (!oleosSelecionados.includes(oleo)) return false
                if (conflitoLimaoMetaPWR && oleo === 'Limão Siciliano') return false
                return true
              }).filter((oleo) => Boolean(INSTRUCOES_ROTINA[oleo]?.manha))
              const oleosDia = OLEOS_DIA.filter((oleo) => {
                if (!oleosSelecionados.includes(oleo)) return false
                if (oleo === 'Limão Siciliano' && !conflitoLimaoMetaPWR) return false
                return true
              }).filter((oleo) => Boolean(INSTRUCOES_ROTINA[oleo]?.dia))
              const oleosNoite = OLEOS_NOITE.filter((oleo) => oleosSelecionados.includes(oleo))
                .filter((oleo) => Boolean(INSTRUCOES_ROTINA[oleo]?.noite))

              if (oleosManha.length === 0 && oleosDia.length === 0 && oleosNoite.length === 0) {
                return null
              }

              return (
                <div className="bg-verde-muito-claro rounded-xl p-5 border-2 border-verde-claro">
                  <h5 className="text-lg font-bold text-verde-escuro mb-4 font-cormorant">
                    Quando usar — rotina diária
                  </h5>

                  <div className="space-y-4">
                    {oleosManha.length > 0 ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#B08968' }}>
                          Manhã — energia, foco e proteção
                        </p>
                        <ul className="space-y-2">
                          {oleosManha.map((oleo) => {
                            const instrucao = INSTRUCOES_ROTINA[oleo]?.manha
                            if (!instrucao) return null
                            const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
                            return (
                              <li key={`manha-${oleo}`} className="flex items-start gap-2 text-sm" style={{ color: '#2C2C2A' }}>
                                <span className="mt-0.5 shrink-0 font-bold" style={{ color: '#52B788' }}>→</span>
                                <span>
                                  <strong>{nomeExibicao}</strong> → {instrucao}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {oleosDia.length > 0 ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#B08968' }}>
                          Dia — equilíbrio emocional e suporte contínuo
                        </p>
                        <ul className="space-y-2">
                          {oleosDia.map((oleo) => {
                            const instrucao = INSTRUCOES_ROTINA[oleo]?.dia
                            if (!instrucao) return null
                            const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
                            return (
                              <li key={`dia-${oleo}`} className="flex items-start gap-2 text-sm" style={{ color: '#2C2C2A' }}>
                                <span className="mt-0.5 shrink-0 font-bold" style={{ color: '#52B788' }}>→</span>
                                <span>
                                  <strong>{nomeExibicao}</strong> → {instrucao}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {oleosNoite.length > 0 ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#B08968' }}>
                          Noite — descanso, relaxamento e regeneração
                        </p>
                        <ul className="space-y-2">
                          {oleosNoite.map((oleo) => {
                            const instrucao = INSTRUCOES_ROTINA[oleo]?.noite
                            if (!instrucao) return null
                            const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
                            return (
                              <li key={`noite-${oleo}`} className="flex items-start gap-2 text-sm" style={{ color: '#2C2C2A' }}>
                                <span className="mt-0.5 shrink-0 font-bold" style={{ color: '#52B788' }}>→</span>
                                <span>
                                  <strong>{nomeExibicao}</strong> → {instrucao}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ) : null}

                    <p className="text-xs text-texto-suave italic mt-2 pt-4 border-t border-bege-medio">
                      <strong>Observação:</strong> cada pessoa é única. Respeite como seu corpo reage a cada óleo e ajuste conforme sua necessidade. Na dúvida, entre em contato para orientação personalizada.
                    </p>
                  </div>
                </div>
              )
            })()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={copiarProtocoloUso}
              className="flex items-center justify-center gap-2 bg-white border-2 border-verde-escuro text-verde-escuro px-4 py-3 rounded-xl hover:bg-verde-muito-claro transition-all font-medium"
            >
              <Copy className="w-4 h-4" />
              Copiar protocolo de uso
            </button>

            <button
              onClick={copiarRotinaDiaria}
              className="flex items-center justify-center gap-2 bg-white border-2 border-verde-escuro text-verde-escuro px-4 py-3 rounded-xl hover:bg-verde-muito-claro transition-all font-medium"
            >
              <Copy className="w-4 h-4" />
              Copiar rotina diária
            </button>

            <button
              onClick={copiarTudo}
              className="flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-3 rounded-xl hover:bg-opacity-90 transition-all font-medium"
            >
              <Copy className="w-4 h-4" />
              Copiar tudo
            </button>

            <button
              onClick={abrirWhatsApp}
              className="flex items-center justify-center gap-2 text-white px-4 py-3 rounded-xl transition-all font-medium"
              style={{ backgroundColor: '#25D366' }}
            >
              <MessageCircle className="w-4 h-4" />
              Abrir no WhatsApp
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
